/**
 * The unlocked wallet: the one place in the page that holds key material.
 *
 * The recovery phrase is decrypted from the wallet file with the password, turned into the BIP39 seed
 * and dropped. The seed stays in this module, out of reach of the view models, until the wallet is
 * locked. A private key is derived from it for one signing action, handed to a SoftwareSigner and
 * disposed with it. The models see the account (address, public key, path) and the sealed file only.
 */

import { openWalletFile, createWalletFile, seedFromMnemonic, deriveAddressOnly, deriveFromSeed, SoftwareSigner } from 'streembit-bc-sdk';
import type { WalletData, DerivedAddress } from 'streembit-bc-sdk';
import { saveStoredWallet } from './store';

export interface Session {
    /** account 0, address 0 of the wallet */
    account: DerivedAddress;
    /** the sealed wallet file the session was opened from */
    file: WalletData;
}

let seed: Uint8Array | null = null;
let session: Session | null = null;

export function currentSession(): Session | null {
    return session;
}

export function requireSession(): Session {
    if (!session) throw new Error('The wallet is locked');
    return session;
}

/** Opens the sealed file with the password and starts the session. */
export async function unlockWithPassword(file: WalletData, password: string): Promise<Session> {
    return unlockWithMnemonic(await openWalletFile(file, password), file);
}

/** Starts the session from a phrase just created or restored, sealed into `file`. */
export function unlockWithMnemonic(mnemonic: string, file: WalletData): Session {
    lockSession();
    seed = seedFromMnemonic(mnemonic);
    session = { account: deriveAddressOnly(seed, 0, 0), file };
    return session;
}

/** Seals a new or restored phrase into a wallet file, keeps the file in this browser and starts the session. */
export async function createWallet(mnemonic: string, password: string, network: string): Promise<Session> {
    const file = await createWalletFile(mnemonic, password, { network });
    const session = unlockWithMnemonic(mnemonic, file);
    await saveStoredWallet(file, session.account.address);
    return session;
}

/** Zeroes the seed and forgets the session. Safe to call when there is none. */
export function lockSession(): void {
    if (seed) seed.fill(0);
    seed = null;
    session = null;
}

/** Derives the signing key for one action and disposes it afterwards. */
export async function withSigner<T>(action: (signer: SoftwareSigner) => Promise<T>): Promise<T> {
    if (!seed) throw new Error('The wallet is locked');
    const derived = deriveFromSeed(seed, 0, 0);
    const signer = new SoftwareSigner(derived.privateKey);
    try {
        return await action(signer);
    } finally {
        signer.dispose();
    }
}
